import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { CareerLinkCard } from 'molecules/CareerLinkCard';
import { careerType } from 'services/careerType';

import { COURSE_CTA_KEY, IPassportCourse } from '../../model/skillPassportCourses';
import { Block, BlockTitle, Meta } from './passportStyles';

/**
 * Паспорт, пункт 6 ТЗ 0071 (п. 6.4): ОДНА рекомендация курса.
 *
 * Карточка — одна ссылка целиком (`CareerLinkCard`): у неё одно назначение,
 * страница курса. Подпись действия внутри — текст, а не вложенная кнопка:
 * интерактивный элемент внутри `<a>` — невалидная разметка.
 *
 * 🔴 Без имён пробелов строка «закрывает» не показывается: «закроет три
 * пробела» без названий ТЗ запрещает прямо, а выдумывать их не из чего.
 *
 * 🔴 Подпись действия зависит от `access`, а не от одного `status`: человеку,
 * который курс уже купил, «Zobacz program kursu» звучит как предложение купить
 * второй раз.
 */
interface ISkillPassportCourse {
  /**
   * `null` — курса, который закрывает этот пробел, нет. Блок всё равно стоит:
   * у навыка ниже требования пустое место на месте курса читается как «здесь
   * не догрузилось», а не как ответ.
   */
  course: IPassportCourse | null;
  /**
   * 0072: подпись действия внутри карточки. В паспорте её нет — действие с
   * курсом уже стоит кнопкой в «Następny krok», вторая подпись дублировала бы.
   */
  showCta?: boolean;
  /** 0073: внутри раскрытого «Chcę się nauczyć» — без своего блока и заголовка. */
  inline?: boolean;
}

export const SkillPassportCourse = memo(
  ({ course, showCta = true, inline = false }: ISkillPassportCourse) => {
    const { t } = useTranslation();

    if (!course && inline) return null;

    if (!course) {
      return (
        <Block aria-labelledby="skm-passport-course">
          <BlockTitle id="skm-passport-course">{t('careerCenter.map.course.title')}</BlockTitle>
          <Meta>{t('careerCenter.map.whereToGetEmpty')}</Meta>
        </Block>
      );
    }

    const why =
      course.reasonCode === 'closes_critical'
        ? t('careerCenter.map.course.whyCritical', { n: course.criticalGaps })
        : course.reasonCode === 'closes_important'
        ? t('careerCenter.map.course.whyImportant', { n: course.importantGaps })
        : '';

    const card = (
      <CareerLinkCard href={course.courseUrl} data-testid="skm-passport-course">
        <Name>{course.courseName}</Name>
        {course.closesSkillNames.length > 0 && (
          <Line>
            {t('careerCenter.map.course.closes', { skills: course.closesSkillNames.join(', ') })}
          </Line>
        )}
        {why && <Line>{why}</Line>}
        {showCta && <Cta>{t(COURSE_CTA_KEY[course.access])}</Cta>}
      </CareerLinkCard>
    );

    if (inline) return card;

    return (
      <Block aria-labelledby="skm-passport-course">
        <BlockTitle id="skm-passport-course">{t('careerCenter.map.course.title')}</BlockTitle>
        {card}
      </Block>
    );
  },
);

const Name = styled.span`
  ${careerType.bodyStrong()};
  display: block;
  color: ${Colors.black};
`;

const Line = styled.span`
  ${careerType.meta()};
  display: block;
  margin-top: 4px;
  color: ${Colors.gray_dark};
`;

/**
 * Подпись действия: подчёркнутый жирный текст, а не жёлтая плашка. Жёлтый в
 * этом разделе — только фон главной кнопки, а карточка уже нажимается целиком;
 * вторая «кнопка» внутри неё обещала бы второе действие.
 */
const Cta = styled.span`
  ${careerType.bodyStrong()};
  display: inline-block;
  margin-top: 8px;
  color: ${Colors.black};
  text-decoration: underline;
`;
